import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './PublicationCaseStudy.module.css';

export default function SupersonicWingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/research')}>
        ← Back to Publications
      </button>

      <header className={styles.header}>
        <div className={styles.badgeRow}>
          <div className={styles.badge} style={{ background: 'rgba(3, 105, 161, 0.1)', color: '#0369a1', border: '1px solid rgba(3, 105, 161, 0.3)' }}>
            🏆 3rd Place — AIAA Student Conference VII, 2025 (Paper Team Category)
          </div>
        </div>
        <h1 className={styles.title}>
          Static Structural Analysis and Material Selection of a Supersonic Fighter Aircraft's Wing
        </h1>
        <div className={styles.authors}>
          Mahi Uddin Ahmaad, Ashiquzzaman Sadi, <span className={styles.highlightAuthor}>Jolok Banarjee</span>, Md Ahad Israq, Saifur Rahman Bakaul
        </div>
        <div className={styles.affiliations}>
          Aviation and Aerospace University, Bangladesh
        </div>

        <div className={styles.headerActions}>
          <a
            href="https://arc.aiaa.org/doi/10.2514/6.2025-106584"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.btnOutline}
          >
            DOI: 10.2514/6.2025-106584
          </a>
          <Link
            to="/projects/skybee-homeland-defense-interceptor"
            className={styles.btnPrimary}
          >
            SkyBee Project Page
          </Link>
        </div>
      </header>

      <figure className={styles.singleFigure}>
        <img
          src="/assets/publication/ucav_structure_material_analysis/AIAA Paper published.png"
          alt="AIAA ARC Publication Record"
        />
        <figcaption>
          Official AIAA ARC record — <a href="https://arc.aiaa.org/doi/10.2514/6.2025-106584" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>View on ARC</a>
        </figcaption>
      </figure>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Abstract</h3>
        <p className={styles.abstractText}>
          An aircraft flying at supersonic speeds must structurally withstand the aerodynamic loads it
          encounters during flight. During design, this capability is assessed through deformation and
          stress analysis. In this study, deformation and stress analysis is carried out on the wing of
          a supersonic unmanned aerial vehicle, which is being designed as a replacement of existing
          supersonic fighter aircrafts. Ti-6Al-4V Titanium Alloy was used in the front spar of the wing
          to provide high strength and stiffness, whereas 7075 T6 Aluminium Alloy was selected for the
          secondary spars and ribs to reduce weight. For the wing skin IM7/977-3 Carbon Fiber Composite
          (Carbon/Epoxy) was selected to provide high strength to weight ratio and thermal stability.
          The stress analysis was performed for two load factors: +1g and +7.5g, which represents the
          positive limit load factors based on the V-n Diagram of the designed supersonic aircraft.
          The simulation results from ANSYS Mechanical demonstrate that the wing structure is capable
          of withstanding the stresses induced by aerodynamic loads throughout the flight envelope.
          The stress applied to the wing is well within the yield strength of the selected materials,
          confirming that it can effectively perform its flight operations.
        </p>
        <div className={styles.keywords}>
          <strong>Keywords:</strong> Structural analysis, material selection, supersonic aircraft, FEA, ANSYS, Ti-6Al-4V, IM7/977-3.
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Aircraft Overview</h2>
        <p className={styles.paragraph}>
          Designed for the <a href="https://aiaa.org/wp-content/uploads/2025/08/2024-2025_team_homeland_defense_interceptor-updated-9-december-2024.pdf?_gl=1*6b0sxx*_gcl_au*MjE2MTUwMjg3LjE3Nzk1MTQ3ODI.*_ga*NDE0NTAxODUuMTc3MTY4OTE0Ng..*_ga_BFMKMMYM72*czE3ODExOTA0MzgkbzE3JGcxJHQxNzgxMTkxNDUxJGo0MSRsMCRoMA..*_ga_GKPDRCLRFH*czE3ODExOTA0MzgkbzE3JGcxJHQxNzgxMTkxNDUxJGo0MSRsMCRoMA.." target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>AIAA 2024/25 Undergraduate Team Aircraft Design Competition</a>, SkyBee is a low-cost supersonic UCAV intended to replace aging manned interceptors for homeland defense.
        </p>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/ucav_structure_material_analysis/3-view of design aircraft.png"
            alt="3-view drawing of SkyBee"
          />
          <figcaption>
            Fig. 1 — 3-view and isometric rendering of SkyBee
          </figcaption>
        </figure>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/ucav_structure_material_analysis/v-n_diagram-of-skybee-at-sea-level.png"
            alt="V-n diagram"
          />
          <figcaption>
            Fig. 2 — V-n Diagram at sea level (V<sub>stall</sub> = 120 kts, V<sub>dive</sub> = 796.5 kts)
          </figcaption>
        </figure>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Hybrid Material Selection</h2>
        <p className={styles.paragraph}>
          A hybrid wing structure was chosen to balance thermal performance, specific strength, stealth, and manufacturability.
        </p>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/ucav_structure_material_analysis/skybee-structure.png"
            alt="Internal structure"
          />
          <figcaption>
            Fig. 3 — Internal structural framework
          </figcaption>
        </figure>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>Ti-6Al-4V</div>
            <div className={styles.metricLabel}>Front Spar</div>
            <p className={styles.metricSubtext}>
              Service temp 347–417°C. Withstands peak bending & thermal loads.
            </p>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>7075-T6</div>
            <div className={styles.metricLabel}>Secondary Spar & Ribs</div>
            <p className={styles.metricSubtext}>
              37% lighter than Ti. 572 MPa UTS. Cost-effective for secondary loads.
            </p>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>IM7/977-3</div>
            <div className={styles.metricLabel}>Wing Skin</div>
            <p className={styles.metricSubtext}>
              2275 MPa tensile strength. Low CTE. Radar-transparent. 177°C dry service.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Analytical & Numerical Results</h2>
        <p className={styles.paragraph}>
          Bending behavior was modeled via elliptic load distribution. Analytical and ANSYS 2021 R1 FEA results showed excellent agreement:
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>2.22%</div>
            <div className={styles.metricLabel}>Error (1g)</div>
            <p className={styles.metricSubtext}>
              45.85 MPa (Analytical) vs 46.87 MPa (FEA)
            </p>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>2.69%</div>
            <div className={styles.metricLabel}>Error (7.5g)</div>
            <p className={styles.metricSubtext}>
              343.91 MPa (Analytical) vs 353.15 MPa (FEA)
            </p>
          </div>
        </div>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/ucav_structure_material_analysis/stress concentraion_FEA analysis 1g and 7.5 g loading.png"
            alt="FEA stress contours"
          />
          <figcaption>
            Fig. 6 — von-Mises stress concentration: (a) 1g, (b) 7.5g
          </figcaption>
        </figure>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>19.42</div>
            <div className={styles.metricLabel}>Factor of Safety (1g)</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>2.58</div>
            <div className={styles.metricLabel}>Factor of Safety (7.5g)</div>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This paper established the structural integrity of a supersonic fighter wing, designed under
          the Homeland Defence Interceptor (HDI) program. In the structural simulation performed in
          ANSYS, under critical load of 1g and 7.5g, confirmed that the structure will remain safe
          within the yield limits. The hybrid material structures selected for the wing ribs, spar, and
          skin integrated into safe wing structure for operation.
        </p>
        <p className={styles.paragraph}>
          Essential next steps may include dynamic and fatigue analysis to predict the operational
          lifespan of the wing. The stress concentrations at the titanium, aluminium, and composite
          interfaces may also be investigated in future work to ensure long-term durability under
          cyclic loading conditions.
        </p>
      </section>

      <section className={styles.footerCtaSection}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1rem 0', fontWeight: 600 }}>
          AIAA Student Conference VII, 2025
        </p>
        <a
          href="https://arc.aiaa.org/doi/10.2514/6.2025-106584"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.btnPrimary}
          style={{ padding: '0.75rem 1.5rem', fontSize: '0.95rem' }}
        >
          🔗 View Publication — DOI: 10.2514/6.2025-106584
        </a>
      </section>

    </div>
  );
}